import { createSelector, weakMapMemoize } from 'reselect';

import { EXTENSIONS_ENUM } from 'constants/ExtensionConstants';
import { FEATURES } from 'constants/FeatureConstants';
import { hasDealerInterventionFullAccess } from 'modules/interventions/selectors';
import {
  hasExtensions,
  hasKioskByDealerId,
  hasMobilityEnabled,
  getSelectedDealerId,
  hasDealerSelfServiceOnly,
  hasFeatures,
  hasFeatureByDealerId
} from 'selectors/dealerSelectors';
import { createDictionary } from 'utils/ObjectUtils';
import { getProps, getRoot } from 'utils/selectors';

import { DASHBOARD_ALLOWED_FIELDS, DASHBOARD_FIELDS, DASHBOARD_TYPE_TO_TAG_FEATURE, DEFAULT_HIDDEN_FIELDS } from '../constants';
import { currentUserHasSelfServiceOnly, getCurrentUserData, getCurrentUserHasExtension, hasCurrentUserFeature } from './currentUser';

export const getDisplaySettings = createSelector(
  getCurrentUserData,
  userData => userData?.additionalData?.displaySettings || {}
);

export const getDashboardSettings = createSelector(
  getDisplaySettings,
  displaySettings => displaySettings.dashboard || {},
  {
    memoize: weakMapMemoize,
  }
);

export const getHiddenFieldsForDashboard = createSelector(
  getProps,
  getDashboardSettings,
  ({ dashboardType }, dashboardSettings) => dashboardSettings[dashboardType]?.hiddenFields ?? [],
  {
    memoize: weakMapMemoize,
  }
);

export const isFieldVisible = createSelector(
  getProps,
  getHiddenFieldsForDashboard,
  ({ fieldName }, hiddenFields) => !hiddenFields.includes(fieldName)
);

export const getVisibleFieldsForDashboard = createSelector(getHiddenFieldsForDashboard, hiddenFields => {
  const allFields = Object.values(DASHBOARD_FIELDS);
  return allFields.filter(field => !hiddenFields.includes(field));
});

export const getDefaultHiddenFieldsForDashboard = dashboardType => DEFAULT_HIDDEN_FIELDS[dashboardType] || [];

export const isCustomizationDrawerLoading = state => state.newUsers.ui.customizationDrawer;

export const shouldDisplayFieldByExtension = createSelector(
  getProps,
  hasExtensions,
  getCurrentUserHasExtension,
  (dealerId, dealerHasExtensions, userHasExtension) => userHasExtension && (!dealerId || dealerHasExtensions)
);

export const shouldDisplayFieldByFeature = createSelector(
  getProps,
  hasFeatures,
  hasCurrentUserFeature,
  (dealerId, dealerHasFeatures, userHasFeature) => userHasFeature && (!dealerId || dealerHasFeatures)
);

export const shouldDisplayStatusField = createSelector(
  getSelectedDealerId,
  hasDealerSelfServiceOnly,
  currentUserHasSelfServiceOnly,
  (dealerId, selfServiceOnlyDealer, selfServiceOnlyUser) =>
    !selfServiceOnlyUser && (!dealerId || !selfServiceOnlyDealer)
);

const FIELD_VISIBILITY = createDictionary(
  {
    // Kanban specific fields
    [DASHBOARD_FIELDS.MOBILITY]: hasMobilityEnabled,
    [DASHBOARD_FIELDS.CUSTOMER_KEY]: hasKioskByDealerId,
    [DASHBOARD_FIELDS.KIOSK_CUSTOMER_PREFERENCE]: hasKioskByDealerId,
    // Intervention dashboard specific fields
    [DASHBOARD_FIELDS.STATUS]: shouldDisplayStatusField,
    [DASHBOARD_FIELDS.ICON_CHECKLIST]: hasDealerInterventionFullAccess,
    [DASHBOARD_FIELDS.STEP]: (state, dealerId) => shouldDisplayFieldByExtension(state, dealerId, EXTENSIONS_ENUM.TRACK),
    [DASHBOARD_FIELDS.SELF_SERVICE_STATUS]: (state, dealerId) =>
      shouldDisplayFieldByExtension(state, dealerId, EXTENSIONS_ENUM.SELF_SERVICE),
    [DASHBOARD_FIELDS.EXTRAS]: (state, dealerId) => hasFeatureByDealerId(state, dealerId, FEATURES.SELF_SERVICE_EXTRAS),
    [DASHBOARD_FIELDS.PROACTIVE_WORKS]: (state, dealerId) => hasFeatureByDealerId(state, dealerId, FEATURES.PROACTIVE),
    [DASHBOARD_FIELDS.TAGS]: (state, dealerId, dashboardType) => shouldDisplayFieldByFeature(state, dealerId, DASHBOARD_TYPE_TO_TAG_FEATURE[dashboardType]),
  },
  () => true
);

export const getAvailableDashboardFields = createSelector(
  getRoot,
  getProps,
  getSelectedDealerId,
  (state, dashboardType, dealerId) => {
    const allFields = DASHBOARD_ALLOWED_FIELDS[dashboardType];
    return allFields.filter(field => FIELD_VISIBILITY[field](state, dealerId, dashboardType));
  }
);
